import { all, takeLatest, call, put, select } from 'redux-saga/effects'
import SeedClassActions from 'modules/domain/seedClass/duck'
import { push } from 'connected-react-router'
import { generatePath } from 'react-router-dom'
import * as managers from './managers'
import SeedClassSelectors from './selectors'
import { SeedClass } from 'modules/domain/seedClass/types'
import SeedClassRoutes from 'views/pages/SeedClass/routes'
import { ListResponse } from 'types/api'
import { updateLocationQuery } from 'modules/sagaHelpers'
import { RequestError } from 'modules/errors'

export const fetchList = function* () {
  try {
    let currentPage = yield select(SeedClassSelectors.page)
    const filter = yield select(SeedClassSelectors.filter)
    const sorting = yield select(SeedClassSelectors.sorting)
    const pageSize = yield select(SeedClassSelectors.pageSize)

    let response: ListResponse<SeedClass> = yield call(
      managers.getSeedClassesList,
      filter,
      sorting,
      currentPage,
      pageSize,
    )
    const pages = Math.ceil(response.total_count / pageSize)
    if (pages !== 0 && pages < currentPage) {
      response = yield call(managers.getSeedClassesList, filter, sorting, pages, pageSize)
      currentPage = pages
    }

    const { data, page, total_count } = response
    yield put(SeedClassActions.listRequestSucceed(data, total_count, page))
    yield call(updateLocationQuery, SeedClassRoutes.List, { page: currentPage })
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedClassActions.listRequestFailed(errType))
  }
}

export const fetchListNext = function* () {
  try {
    const page = yield select(SeedClassSelectors.page)
    const filter = yield select(SeedClassSelectors.filter)
    const sorting = yield select(SeedClassSelectors.sorting)
    const pageSize = yield select(SeedClassSelectors.pageSize)
    const { data, total_count }: { data: SeedClass[]; total_count: number } = yield call(
      managers.getSeedClassesList,
      filter,
      sorting,
      page,
      pageSize,
    )
    yield put(SeedClassActions.listRequestNextSucceed(data, total_count))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedClassActions.listRequestNextFailed(errType))
  }
}

export const fetchSeedClass = function* ({ payload: id }: ReturnType<typeof SeedClassActions.itemRequested>) {
  try {
    const seedClass: SeedClass = yield call(managers.getSeedClass, id)
    yield put(SeedClassActions.itemRequestSucceed(seedClass))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedClassActions.itemRequestFailed(id, errType))
  }
}

export const addSeedClass = function* ({ payload: dto }: ReturnType<typeof SeedClassActions.addRequested>) {
  try {
    const seedClass: SeedClass = yield call(managers.addSeedClass, dto)
    yield put(SeedClassActions.addSucceed(seedClass))
    yield put(push(generatePath(SeedClassRoutes.Edit, { id: seedClass.id })))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedClassActions.addFailed(errType))
  }
}

export const updateSeedClass = function* ({ payload: [id, dto] }: ReturnType<typeof SeedClassActions.updateRequested>) {
  try {
    const seedClass: SeedClass = yield call(managers.updateSeedClass, id, dto)
    yield put(SeedClassActions.updateSucceed(seedClass))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedClassActions.updateFailed(id, errType))
  }
}

export const removeSeedClass = function* ({ payload }: ReturnType<typeof SeedClassActions.removeRequested>) {
  try {
    yield call(managers.removeSeedClass, payload)
    yield put(SeedClassActions.removeSucceed(payload))
    yield put(push(SeedClassRoutes.List))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(SeedClassActions.removeFailed(payload, errType))
  }
}

const SeedClassSagas = function* () {
  yield all([
    takeLatest(SeedClassActions.itemRequested.type, fetchSeedClass),

    takeLatest(SeedClassActions.listRequested.type, fetchList),
    takeLatest(SeedClassActions.filterUpdated.type, fetchList),
    takeLatest(SeedClassActions.sortingUpdated.type, fetchList),
    takeLatest(SeedClassActions.filterHasBeenReset.type, fetchList),
    takeLatest(SeedClassActions.sortingHasBeenReset.type, fetchList),

    takeLatest(SeedClassActions.listRequestedNext.type, fetchListNext),

    takeLatest(SeedClassActions.addRequested.type, addSeedClass),
    takeLatest(SeedClassActions.updateRequested.type, updateSeedClass),
    takeLatest(SeedClassActions.removeRequested.type, removeSeedClass),
  ])
}

export default SeedClassSagas
